import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Card, CardContent, Typography, Button, Grid, Box,
  Stack, Skeleton, Fade, Paper, Divider
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  BarElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  BarElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

export default function SalesDashboard() {
  const navigate = useNavigate();
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Convert string dates to Date objects for the pickers
  const [dateRange, setDateRange] = useState({
    startDate: dayjs().subtract(30, 'day').format('YYYY-MM-DD'), // Last 30 days
    endDate: dayjs().format('YYYY-MM-DD') // Today
  });

  // Dayjs objects for the date pickers
  const [startDateObj, setStartDateObj] = useState(dayjs().subtract(30, 'day'));
  const [endDateObj, setEndDateObj] = useState(dayjs());
  const [selectedDatePreset, setSelectedDatePreset] = useState('30days');

  useEffect(() => {
    document.title = `Sales Dashboard - ${process.env.REACT_APP_SITE_NAME || 'Admin'}`;
    fetchReportData();
  }, [dateRange]);

  // Handler for start date change
  const handleStartDateChange = (newDate) => {
    if (newDate) {
      setStartDateObj(newDate);
      // Format date for API
      const formattedDate = newDate.format('YYYY-MM-DD');
      setDateRange(prev => ({
        ...prev,
        startDate: formattedDate
      }));
    }
  };

  // Handler for end date change
  const handleEndDateChange = (newDate) => {
    if (newDate) {
      setEndDateObj(newDate);
      // Format date for API
      const formattedDate = newDate.format('YYYY-MM-DD');
      setDateRange(prev => ({
        ...prev,
        endDate: formattedDate
      }));
    }
  };

  // Preset date range options
  const applyDatePreset = (days) => {
    const end = dayjs();
    const start = dayjs().subtract(days, 'day');

    setStartDateObj(start);
    setEndDateObj(end);

    setDateRange({
      startDate: start.format('YYYY-MM-DD'),
      endDate: end.format('YYYY-MM-DD')
    });
  };

  const fetchReportData = async () => {
    setLoading(true);
    try {
      const response = await axios({
        method: 'get',
        url: `${process.env.REACT_APP_SHOP_API_URL}/app-admin/reports/sales`,
        params: {
          start_date: dateRange.startDate,
          end_date: dateRange.endDate
        },
        headers: {
          'Authorization': 'Bearer ' + localStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });
      setReportData(response.data);
    } catch (error) {
      console.error('Error fetching report data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 2
    }).format(value || 0);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('th-TH').format(value || 0);
  };

  const getPercentChange = (current, previous) => {
    if (!previous) return 0;
    return ((current - previous) / previous * 100).toFixed(2);
  };

  // Define chart colors
  const chartColors = {
    primary: '#3730a3',
    secondary: '#facc15',
    tertiary: '#10b981',
    quaternary: '#f43f5e',
    background: '#f9fafb',
    cardBg: '#ffffff',
    border: '#e5e7eb',
    text: '#1f2937',
    lightText: '#6b7280'
  };

  // Skeleton loaders for each component
  const SummaryCardSkeleton = () => (
    <Card sx={{ height: '100%', borderRadius: 2, boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' }}>
      <CardContent>
        <Skeleton variant="text" width="70%" height={24} />
        <Skeleton variant="text" width="50%" height={40} sx={{ my: 1 }} />
        <Skeleton variant="rounded" width="40%" height={30} />
      </CardContent>
    </Card>
  );

  const ChartSkeleton = () => (
    <Card sx={{ height: '100%', borderRadius: 2, boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' }}>
      <CardContent>
        <Skeleton variant="text" width="50%" height={32} />
        <Skeleton variant="rectangular" width="100%" height={200} sx={{ mt: 2 }} />
      </CardContent>
    </Card>
  );

  const TableSkeleton = () => (
    <Card sx={{ height: '100%', borderRadius: 2, boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' }}>
      <CardContent>
        <Skeleton variant="text" width="50%" height={32} />
        <Box sx={{ mt: 2 }}>
          {[...Array(5)].map((_, i) => (
            <Box key={i} display="flex" justifyContent="space-between" alignItems="center" my={1}>
              <Skeleton variant="text" width="60%" height={24} />
              <Skeleton variant="text" width="15%" height={24} />
              <Skeleton variant="text" width="15%" height={24} />
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{
      padding: 0,
      backgroundColor: chartColors.background,
      minHeight: '100vh'
    }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          backgroundColor: chartColors.cardBg,
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
        }}
      >
        <Box
          display="flex"
          flexDirection={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          mb={3}
        >

          <Box
            display="flex"
            flexDirection={{ xs: 'column', sm: 'row' }}
            alignItems="center"
            gap={2}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box display="flex" alignItems="center" gap={1} flexWrap={{ xs: 'wrap', sm: 'nowrap' }}>
                <DatePicker
                  label="Start Date"
                  value={startDateObj}
                  onChange={handleStartDateChange}
                  format="DD/MM/YYYY"
                  maxDate={endDateObj}
                  slotProps={{
                    textField: {
                      variant: "outlined",
                      size: "small",
                      InputProps: { sx: { borderRadius: 1 } }
                    }
                  }}
                />
                <Typography variant="body1" color={chartColors.lightText}>to</Typography>
                <DatePicker
                  label="End Date"
                  value={endDateObj}
                  onChange={handleEndDateChange}
                  format="DD/MM/YYYY"
                  minDate={startDateObj}
                  maxDate={dayjs()}
                  slotProps={{
                    textField: {
                      variant: "outlined",
                      size: "small",
                      InputProps: { sx: { borderRadius: 1 } }
                    }
                  }}
                />
              </Box>


            </LocalizationProvider>
          </Box>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            sx={{ mt: { xs: 2, sm: 0 } }}
          >
            <Button
              size="small"
              variant={selectedDatePreset === '7days' ? 'contained' : 'outlined'}
              sx={{
                borderRadius: 1,
                backgroundColor: selectedDatePreset === '7days' ? chartColors.primary : 'white',
                color: selectedDatePreset === '7days' ? 'white' : chartColors.text,
                '&:hover': {
                  backgroundColor: selectedDatePreset === '7days' ? '#2e279d' : 'rgba(55, 48, 163, 0.04)'
                }
              }}
              onClick={() => {
                applyDatePreset(7)
                setSelectedDatePreset('7days');
              }}
            >
              Last 7 Days
            </Button>
            <Button
              size="small"
              variant={selectedDatePreset === '30days' ? 'contained' : 'outlined'}
              sx={{
                borderRadius: 1,
                backgroundColor: selectedDatePreset === '30days' ? chartColors.primary : 'white',
                color: selectedDatePreset === '30days' ? 'white' : chartColors.text,
                '&:hover': {
                  backgroundColor: selectedDatePreset === '30days' ? '#2e279d' : 'rgba(55, 48, 163, 0.04)'
                }
              }}

              onClick={() => {
                applyDatePreset(30)
                setSelectedDatePreset('30days');
              }}
            >
              Last 30 Days
            </Button>
            <Button
              size="small"
              variant={selectedDatePreset === '90days' ? 'contained' : 'outlined'}
              sx={{
                borderRadius: 1,
                backgroundColor: selectedDatePreset === '90days' ? chartColors.primary : 'white',
                color: selectedDatePreset === '90days' ? 'white' : chartColors.text,
                '&:hover': {
                  backgroundColor: selectedDatePreset === '90days' ? '#2e279d' : 'rgba(55, 48, 163, 0.04)'
                }
              }}
              onClick={() => {
                applyDatePreset(90)
                setSelectedDatePreset('90days');
              }}
            >
              Last 3 Months
            </Button>
          </Stack>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {loading && (
          <Box sx={{ opacity: 0.8 }}>
            <Grid container spacing={3} className="mb-6">
              {[...Array(4)].map((_, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <SummaryCardSkeleton />
                </Grid>
              ))}
            </Grid>

            <Grid container spacing={3} className="mb-6">
              <Grid item xs={12} md={6}>
                <ChartSkeleton />
              </Grid>
              <Grid item xs={12} md={6}>
                <ChartSkeleton />
              </Grid>
            </Grid>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <ChartSkeleton />
              </Grid>
              <Grid item xs={12} md={6}>
                <TableSkeleton />
              </Grid>
            </Grid>
          </Box>
        )}

        <Fade in={!loading}>
          <Box>
            {reportData && (
              <>
                {/* Summary Cards */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  {/* Total Sales Card */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{
                      height: '100%',
                      backgroundColor: chartColors.primary,
                      color: 'white',
                      borderRadius: 2,
                      boxShadow: '0 10px 15px -3px rgba(55, 48, 163, 0.3), 0 4px 6px -2px rgba(55, 48, 163, 0.2)'
                    }}>
                      <CardContent>
                        <Box display="flex">
                          <Box width="60%">
                            <Typography variant="body2" sx={{ opacity: 0.9 }} gutterBottom>
                              Total Sales ({formatNumber(reportData.sales_summary.total_orders)} orders)
                            </Typography>
                            <Typography variant="h4" component="div" fontWeight="bold">
                              {formatCurrency(reportData.sales_summary.net_sales)}
                            </Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              sx={{
                                mt: 2,
                                color: 'white',
                                borderColor: 'rgba(255,255,255,0.3)',
                                borderRadius: 1,
                                '&:hover': {
                                  borderColor: 'white',
                                  backgroundColor: 'rgba(255,255,255,0.1)'
                                }
                              }}
                              onClick={() => {
                                navigate('/order-from-web', {
                                  state: {
                                    startDate: dateRange.startDate,
                                    endDate: dateRange.endDate
                                  }
                                });
                              }}
                            >
                              View Details
                            </Button>
                          </Box>
                          <Box width="40%">
                            {reportData.daily_sales && reportData.daily_sales.length > 0 && (
                              <Line
                                options={{

                                  elements: {
                                    point: { radius: 0 },
                                    line: { tension: 0.4 }
                                  },
                                  plugins: { legend: { display: false } },
                                  scales: {
                                    x: { display: false },
                                    y: { display: false }
                                  }
                                }}
                                data={{
                                  labels: reportData.daily_sales.map(day => day.date),
                                  datasets: [{
                                    data: reportData.daily_sales.map(day => day.revenue),
                                    borderColor: 'white',
                                    backgroundColor: 'rgba(255,255,255,0.1)',
                                    fill: true
                                  }]
                                }}
                              />
                            )}
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Revenue Card */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{
                      height: '100%',
                      backgroundColor: chartColors.secondary,
                      borderRadius: 2,
                      boxShadow: '0 10px 15px -3px rgba(250, 204, 21, 0.2), 0 4px 6px -2px rgba(250, 204, 21, 0.1)'
                    }}>
                      <CardContent>
                        <Box display="flex">
                          <Box width="60%">
                            <Typography variant="body2" gutterBottom color={chartColors.text}>
                              Revenue (Completed Orders)
                            </Typography>
                            <Typography variant="h4" component="div" fontWeight="bold" color={chartColors.text}>
                              {formatCurrency(reportData.sales_summary.revenue)}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1 }} color={chartColors.text}>
                              {formatNumber(reportData.sales_summary.completed_orders)} paid orders
                            </Typography>
                          </Box>
                          <Box width="40%" display="flex" alignItems="center" justifyContent="center">
                            <Box
                              textAlign="center"
                              sx={{
                                backgroundColor: 'rgba(255,255,255,0.7)',
                                borderRadius: 2,
                                p: 1,
                                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                              }}
                            >
                              <Typography variant="h5" component="div" color={chartColors.primary} fontWeight="bold">
                                {reportData.sales_summary.completed_orders > 0
                                  ? Math.round(reportData.sales_summary.completed_orders / reportData.sales_summary.total_orders * 100)
                                  : 0}%
                              </Typography>
                              <Typography variant="body2" color={chartColors.text}>
                                Completion Rate
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Average Order Value */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{
                      height: '100%',
                      borderRadius: 2,
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                    }}>
                      <CardContent>
                        <Typography variant="body2" color={chartColors.lightText} gutterBottom>
                          Average Order Value
                        </Typography>
                        <Typography variant="h4" component="div" color={chartColors.primary} fontWeight="bold">
                          {formatCurrency(reportData.average_order_value)}
                        </Typography>
                        <Box
                          mt={2}
                          p={1.5}
                          bgcolor="rgba(16, 185, 129, 0.1)"
                          borderRadius={1}
                        >
                          <Typography variant="body2" color={chartColors.tertiary}>
                            Total Discounts: {formatCurrency(reportData.sales_summary.total_discounts)}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Order Status */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{
                      height: '100%',
                      borderRadius: 2,
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                    }}>
                      <CardContent>
                        <Typography variant="body2" color={chartColors.lightText} gutterBottom>
                          Order Status Breakdown
                        </Typography>
                        <Box mt={2}>
                          {reportData.order_statuses && reportData.order_statuses.map((status, index) => (
                            <Box
                              key={index}
                              mb={1.5}
                              display="flex"
                              justifyContent="space-between"
                              sx={{
                                p: 1,
                                borderRadius: 1,
                                backgroundColor: index % 2 === 0 ? 'rgba(55, 48, 163, 0.05)' : 'transparent'
                              }}
                            >
                              <Typography variant="body2" color={chartColors.text}>
                                {status.order_status}:
                              </Typography>
                              <Typography variant="body2" fontWeight="bold" color={chartColors.primary}>
                                {formatNumber(status.count)}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Charts Section */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  {/* Daily Sales Chart */}
                  <Grid item xs={12} md={6}>
                    <Card sx={{
                      height: '100%',
                      borderRadius: 2,
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                    }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom color={chartColors.text} fontWeight="600">
                          Daily Sales
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        {reportData.daily_sales && reportData.daily_sales.length > 0 ? (
                          <Bar
                            options={{
                              plugins: {
                                legend: {
                                  position: 'top',
                                },
                                tooltip: {
                                  callbacks: {
                                    label: function (context) {
                                      return `Revenue: ${formatCurrency(context.raw)}`;
                                    }
                                  }
                                }
                              },
                              scales: {
                                y: {
                                  beginAtZero: true,
                                  ticks: {
                                    callback: function (value) {
                                      return formatCurrency(value);
                                    }
                                  }
                                }
                              }
                            }}
                            data={{
                              labels: reportData.daily_sales.map(day => day.date),
                              datasets: [
                                {
                                  label: 'Daily Revenue',
                                  data: reportData.daily_sales.map(day => day.revenue),
                                  backgroundColor: chartColors.primary,
                                  borderRadius: 4
                                }
                              ],
                            }}
                            height={200}
                          />
                        ) : (
                          <Box textAlign="center" my={5} p={3} bgcolor="rgba(55, 48, 163, 0.05)" borderRadius={2}>
                            <Typography variant="body1" color={chartColors.lightText}>
                              No daily sales data available
                            </Typography>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Payment Methods Chart */}
                  <Grid item xs={12} md={6}>
                    <Card sx={{
                      height: '100%',
                      borderRadius: 2,
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                    }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom color={chartColors.text} fontWeight="600">
                          Payment Methods
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        {reportData.payment_methods && reportData.payment_methods.length > 0 ? (
                          <Bar
                            options={{

                              indexAxis: 'y',
                              plugins: {
                                legend: {
                                  display: false
                                },
                                tooltip: {
                                  callbacks: {
                                    label: function (context) {
                                      return `Revenue: ${formatCurrency(context.raw)}`;
                                    }
                                  }
                                }
                              },
                              scales: {
                                x: {
                                  beginAtZero: true,
                                  ticks: {
                                    callback: function (value) {
                                      return formatCurrency(value);
                                    }
                                  }
                                }
                              }
                            }}
                            data={{
                              labels: reportData.payment_methods.map(method => {
                                // Map payment method codes to readable names
                                const methodNames = {
                                  'cre': 'Credit Card',
                                  'tra': 'Bank Transfer',
                                  'cod': 'Cash on Delivery'
                                };
                                return methodNames[method.order_payment_method] || method.order_payment_method;
                              }),
                              datasets: [
                                {
                                  data: reportData.payment_methods.map(method => method.total),
                                  backgroundColor: [
                                    chartColors.primary,
                                    chartColors.secondary,
                                    chartColors.tertiary,
                                    chartColors.quaternary
                                  ],
                                  borderRadius: 4
                                }
                              ],
                            }}
                            height={200}
                          />
                        ) : (
                          <Box textAlign="center" my={5} p={3} bgcolor="rgba(55, 48, 163, 0.05)" borderRadius={2}>
                            <Typography variant="body1" color={chartColors.lightText}>
                              No payment method data available
                            </Typography>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Top Products and Customers */}
                <Grid container spacing={3}>
                  {/* Top Products */}
                  <Grid item xs={12} md={6}>
                    <Card sx={{
                      borderRadius: 2,
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                    }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom color={chartColors.text} fontWeight="600">
                          Top Selling Products
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        {reportData.top_products && reportData.top_products.length > 0 ? (
                          <Bar
                            options={{

                              indexAxis: 'y',
                              plugins: {
                                legend: {
                                  display: false
                                }
                              },
                              scales: {
                                x: {
                                  beginAtZero: true,
                                  title: {
                                    display: true,
                                    text: 'Units Sold',
                                    color: chartColors.lightText
                                  }
                                }
                              }
                            }}
                            data={{
                              labels: reportData.top_products.map(product =>
                                product.name.length > 25 ? product.name.substring(0, 25) + '...' : product.name
                              ),
                              datasets: [
                                {
                                  data: reportData.top_products.map(product => product.quantity_sold),
                                  backgroundColor: chartColors.secondary,
                                  borderRadius: 4
                                }
                              ],
                            }}
                            height={200}
                          />
                        ) : (
                          <Box textAlign="center" my={5} p={3} bgcolor="rgba(55, 48, 163, 0.05)" borderRadius={2}>
                            <Typography variant="body1" color={chartColors.lightText}>
                              No product data available
                            </Typography>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Top Customers */}
                  <Grid item xs={12} md={6}>
                    <Card sx={{
                      borderRadius: 2,
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                    }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom color={chartColors.text} fontWeight="600">
                          Top Customers
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        {reportData.top_customers && reportData.top_customers.length > 0 ? (
                          <Box sx={{ overflow: 'auto', maxHeight: 550 }}>
                            <Box sx={{
                              width: '100%',
                              borderCollapse: 'collapse',
                              display: 'table'
                            }}>
                              <Box sx={{
                                display: 'table-header-group',
                                backgroundColor: 'rgba(55, 48, 163, 0.05)'
                              }}>
                                <Box sx={{ display: 'table-row' }}>
                                  <Box sx={{
                                    display: 'table-cell',
                                    p: 2,
                                    borderBottom: `1px solid ${chartColors.border}`,
                                    color: chartColors.text,
                                    fontWeight: 'bold'
                                  }}>
                                    Customer
                                  </Box>
                                  <Box sx={{
                                    display: 'table-cell',
                                    p: 2,
                                    textAlign: 'right',
                                    borderBottom: `1px solid ${chartColors.border}`,
                                    color: chartColors.text,
                                    fontWeight: 'bold'
                                  }}>
                                    Orders
                                  </Box>
                                  <Box sx={{
                                    display: 'table-cell',
                                    p: 2,
                                    textAlign: 'right',
                                    borderBottom: `1px solid ${chartColors.border}`,
                                    color: chartColors.text,
                                    fontWeight: 'bold'
                                  }}>
                                    Total Spent
                                  </Box>
                                </Box>
                              </Box>
                              <Box sx={{ display: 'table-row-group' }}>
                                {reportData.top_customers.map((customer, index) => (
                                  <Box
                                    key={index}
                                    sx={{
                                      display: 'table-row',
                                      backgroundColor: index % 2 === 0 ? 'white' : 'rgba(55, 48, 163, 0.03)'
                                    }}
                                  >
                                    <Box sx={{
                                      display: 'table-cell',
                                      p: 2,
                                      borderBottom: `1px solid ${chartColors.border}`,
                                      color: chartColors.text
                                    }}>
                                      {customer.user_email}
                                    </Box>
                                    <Box sx={{
                                      display: 'table-cell',
                                      p: 2,
                                      textAlign: 'right',
                                      borderBottom: `1px solid ${chartColors.border}`,
                                      color: chartColors.text,
                                      fontWeight: 'medium'
                                    }}>
                                      {formatNumber(customer.order_count)}
                                    </Box>
                                    <Box sx={{
                                      display: 'table-cell',
                                      p: 2,
                                      textAlign: 'right',
                                      borderBottom: `1px solid ${chartColors.border}`,
                                      color: chartColors.primary,
                                      fontWeight: 'bold'
                                    }}>
                                      {formatCurrency(customer.total_spent)}
                                    </Box>
                                  </Box>
                                ))}
                              </Box>
                            </Box>
                          </Box>
                        ) : (
                          <Box textAlign="center" my={5} p={3} bgcolor="rgba(55, 48, 163, 0.05)" borderRadius={2}>
                            <Typography variant="body1" color={chartColors.lightText}>
                              No customer data available
                            </Typography>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </>
            )}

            {!reportData && !loading && (
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                height="50vh"
                sx={{
                  backgroundColor: 'rgba(55, 48, 163, 0.05)',
                  borderRadius: 2,
                  p: 4
                }}
              >
                <Typography variant="h6" color={chartColors.text} mb={2}>
                  No report data available
                </Typography>
                <Typography variant="body1" color={chartColors.lightText} mb={3} textAlign="center">
                  Please check your connection or try a different date range.
                </Typography>
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: chartColors.primary,
                    '&:hover': {
                      backgroundColor: '#2e279d'
                    }
                  }}
                  onClick={fetchReportData}
                >
                  Retry
                </Button>
              </Box>
            )}
          </Box>
        </Fade>
      </Paper>
    </Box>
  );
}